import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalPayload, IModalsStore} from "data/stores/modals/modals.store";
import {ModalType, RequestState} from "data/enums";
import {every, isEqual} from "lodash";
import {ChangeEvent, SyntheticEvent} from "react";
import {AxiosError} from "axios";
import {IApiResponse} from "data/services/http";
import {extractErrorMessage} from "data/utils";
import type {IUserStore} from "data/stores/user/user.store";

interface IRegistrationFormElement extends HTMLFormElement {
	username: HTMLInputElement;
	terms: HTMLInputElement;
}
export interface IModalSecondaryRegistrationController extends ViewController {
	i18n: ILocalizationStore;

	get isOpen(): boolean;
	get modalContent(): IModalPayload | null;
	get isFormDisabled(): boolean;
	get isFormDisabledByData(): boolean;
	get error(): Record<string, string> | null;
	get username(): string;
	get mlbUserName(): string;

	close: () => void;
	handleFormSubmit: (event: SyntheticEvent<IRegistrationFormElement>) => void;
	handleFormOnChange: (event: ChangeEvent<IRegistrationFormElement>) => void;
	handleCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
	handleInputUserNameValue: (event: ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class ModalSecondaryRegistrationController implements IModalSecondaryRegistrationController {
	@observable private _requestState = RequestState.IDLE;
	@observable private _errorMsg: string | null = null;
	@observable private _errorPlace = "";
	@observable private _username: string = "";
	@observable private _terms: boolean = false;

	get error() {
		if (!this._errorMsg) return null;

		return {
			[this._errorPlace || "common"]: this._errorMsg,
		};
	}

	get mlbUserName() {
		if (!this._userStore.user) return "";

		return this._userStore.user.username;
	}

	get username(): string {
		return this._username;
	}

	get isFormDisabled(): boolean {
		return isEqual(this._requestState, RequestState.PENDING);
	}

	get isFormDisabledByData(): boolean {
		return every([!this._terms, !this._username.length]);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.SECONDARY_REGISTRATION;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	@action
	private reportError(error: string, place: string = "") {
		this._errorMsg = error;
		this._errorPlace = place;

		return true;
	}

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._errorMsg = extractErrorMessage(error);
		this._requestState = RequestState.ERROR;

		this._modalsStore.showModal(ModalType.ERROR, {
			message: this._errorMsg,
		});
	};

	@action public handleFormSubmit = (event: SyntheticEvent<IRegistrationFormElement>) => {
		event.preventDefault();

		const {terms, username} = event.currentTarget;

		const errorInvalidUsername = this.i18n.t(
			"registration.display_name.error",
			"Please provide your display name"
		);

		const errorInvalidTerms = this.i18n.t(
			"registration.terms.error",
			"Please accept Terms & Conditions"
		);

		const validateList = [
			{field: username, error: errorInvalidUsername, place: "username"},
			{field: terms, error: errorInvalidTerms, place: "terms"},
		];

		const hasError = validateList.find(({field, error, place}) =>
			field.checkValidity() ? false : this.reportError(error, place)
		);

		if (hasError) {
			return;
		}

		const payload = {
			username: this._username,
		};

		this._requestState = RequestState.PENDING;
		this._userStore.update(payload).catch(this.onError);
	};

	@action handleFormOnChange = (event: ChangeEvent<IRegistrationFormElement>) => {
		if (event.target.name !== "password") {
			this._errorMsg = null;
			this._errorPlace = "";
		}
	};

	@action private setFormData = (checked: boolean) => {
		this._terms = checked;
	};

	@action handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
		const {checked} = event.target;

		this.setFormData(checked);
	};

	@action handleInputUserNameValue = (event: ChangeEvent<HTMLInputElement>) => {
		this._username = event.target.value.replace("@", "");
	};

	close = () => {
		this._modalsStore.hideModal();
	};
}
